import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const GARAGE = "https://images.unsplash.com/photo-1676018366904-c083ed678e60?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400";

const safeNext = () => {
  const n = new URLSearchParams(window.location.search).get("next") || "/app";
  return n.startsWith("/") && !n.startsWith("//") ? n : "/app";
};

const googleLogin = () => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + safeNext();
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const Field = ({ id, label, ...props }) => (
  <div className="space-y-2">
    <Label htmlFor={id} className="text-xs uppercase tracking-widest text-muted-foreground">{label}</Label>
    <Input id={id} className="h-11 rounded-sm bg-[#0B0F17]" {...props} />
  </div>
);

function AuthForm({ mode }) {
  const { checkAuth } = useAuth();
  const navigate = useNavigate();
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const next = safeNext();
    setBusy(true);
    setError("");
    try {
      const body = mode === "register" ? f : { email: f.email, password: f.password };
      const { data } = await api.post(`/auth/${mode}`, body);
      await checkAuth();
      toast.success(`Signed in as ${data.email}`);
      navigate(next, { replace: true });
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid={`${mode}-form`}>
      {mode === "register" && <Field id="name" label="Name" value={f.name} onChange={set("name")} required data-testid="register-name-input" />}
      <Field id={`${mode}-email`} label="Email" type="email" value={f.email} onChange={set("email")} required data-testid={`${mode}-email-input`} />
      <Field id={`${mode}-password`} label="Password" type="password" value={f.password} onChange={set("password")} required minLength={mode === "register" ? 6 : undefined} data-testid={`${mode}-password-input`} />
      {error && <p className="text-sm text-red-400" data-testid={`${mode}-error`}>{error}</p>}
      <Button type="submit" disabled={busy} className="w-full h-11 rounded-sm font-semibold hover:bg-orange-600 hover:text-white" data-testid={`${mode}-submit-btn`}>
        {busy ? "Please wait..." : mode === "register" ? "Create account" : "Sign in"}
      </Button>
    </form>
  );
}

export default function Login() {
  const { user } = useAuth();
  const navigate = useNavigate();
  useEffect(() => { if (user) navigate(safeNext(), { replace: true }); }, [user, navigate]);

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <div className="flex-1 grid lg:grid-cols-2">
        <div className="relative hidden lg:block">
          <img src={GARAGE} alt="Garage" className="absolute inset-0 h-full w-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F17] to-transparent" />
          <div className="absolute bottom-12 left-12 right-12">
            <div className="hazard h-2 w-24 mb-6" />
            <h2 className="text-5xl font-extrabold uppercase leading-none">Find the part.<br />Beat the price.</h2>
          </div>
        </div>
        <div className="flex items-center justify-center px-4 py-16 grid-bg">
          <div className="w-full max-w-md bg-card border border-border rounded-sm p-8 rise">
            <h1 className="text-4xl font-extrabold uppercase mb-1">Shop access</h1>
            <p className="text-sm text-muted-foreground mb-8">Sign in to search and save your parts jobs.</p>
            <Button variant="outline" onClick={googleLogin} className="w-full h-11 rounded-sm mb-6 hover:bg-secondary hover:text-white" data-testid="google-login-btn">
              <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24"><path fill="#EA4335" d="M12 10.2v3.9h5.4c-.2 1.3-1.6 3.8-5.4 3.8-3.2 0-5.9-2.7-5.9-6s2.7-6 5.9-6c1.9 0 3.1.8 3.8 1.5l2.6-2.5C16.8 3.4 14.6 2.4 12 2.4 6.7 2.4 2.4 6.7 2.4 12s4.3 9.6 9.6 9.6c5.5 0 9.2-3.9 9.2-9.4 0-.6-.1-1.1-.2-1.6H12z"/></svg>
              Continue with Google
            </Button>
            <div className="flex items-center gap-3 mb-6 text-xs uppercase tracking-widest text-muted-foreground">
              <div className="h-px flex-1 bg-border" /> or email <div className="h-px flex-1 bg-border" />
            </div>
            <Tabs defaultValue="login">
              <TabsList className="grid grid-cols-2 w-full mb-6 rounded-sm">
                <TabsTrigger value="login" className="rounded-sm" data-testid="tab-login">Sign in</TabsTrigger>
                <TabsTrigger value="register" className="rounded-sm" data-testid="tab-register">Create account</TabsTrigger>
              </TabsList>
              <TabsContent value="login"><AuthForm mode="login" /></TabsContent>
              <TabsContent value="register"><AuthForm mode="register" /></TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
}
